import { Direction } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { SMS } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { createJSONStorage } from 'zustand/middleware';
import { createShallowStore, createStoreWithPersistAndSubscribe } from '@frontend/store';
import { pendo } from '@frontend/tracking';
import { MessagingUIType } from './types';

type FlowStatus = 'Complete' | 'Read Only';
type FlowType = 'New Manual SMS Sent' | 'New SMS Scheduled' | 'Scheduled Message Sent' | 'Reschedule Message';
type ReportTTSArgs<T extends FlowStatus> = {
  timeAtInitialOpen: ThreadState['timeAtInitialOpen'];
  flowStatus: T;
  forcedTimestamp?: number;
} & (T extends 'Complete'
  ? {
      flowType: FlowType;
    }
  : { flowType?: undefined | never });
type PendoTTSReportMetadata<T extends FlowStatus> = {
  secondsToSend: string;
  browserWidth: string;
  flowStatus: FlowStatus;
  flowType?: T extends 'Complete' ? FlowType : never;
};

const convertMaxTimeToTimestamp = (maxTime: number): string => {
  return new Date(Date.now() - maxTime).toISOString();
};
/**
 * Reports Time To Send (TTS) metrics to Pendo
 * @param timeAtInitialOpen - Time when the thread was initially opened
 * @param flowStatus - Status of the flow ('Complete' or 'Read Only')
 * @param flowType - Type of flow when status is 'Complete': 'New Manual SMS Sent' | 'New SMS Scheduled' | 'Scheduled Message Sent' | 'Reschedule Message';
 * @param forcedTimestamp (optional) - Timestamp to override the current time
 */
const sendPendoTTSEvent = <T extends FlowStatus>({
  timeAtInitialOpen,
  flowStatus,
  flowType,
  forcedTimestamp: timestamp = Date.now(),
}: ReportTTSArgs<T>) => {
  if (timeAtInitialOpen === undefined) return;
  const secondsToSend = `${((timestamp - timeAtInitialOpen) / 1000).toFixed(1)}s`;
  const metadata: PendoTTSReportMetadata<T> = {
    secondsToSend,
    browserWidth: JSON.stringify(document.body.clientWidth),
    flowStatus,
  };
  if (flowStatus === 'Complete' && flowType) {
    metadata.flowType = flowType as PendoTTSReportMetadata<T>['flowType'];
  }
  pendo.track('sms_thread_tts', metadata);
};

const MAX_TIME_TO_OPEN = 1000 * 60 * 10; // 10 minutes

type ThreadState = {
  timeAtInitialOpen: number | undefined;
  hasReportedTTS: boolean;
  reportedSmsId: string;
  uiTypes: MessagingUIType[];
};

export type OpenThreadsStore = {
  MAX_TIME_TO_OPEN: number;
  openThreads: Record<string, ThreadState>;
  reportThreadOpen: (lastSms: SMS | undefined, uiType: MessagingUIType) => void;
  reportThreadClose: (threadId: string, uiType: MessagingUIType, forcedTimestamp?: number) => void;
  reportTTS: (
    threadId: string,
    flowType: 'New Manual SMS Sent' | 'New SMS Scheduled' | 'Scheduled Message Sent' | 'Reschedule Message'
  ) => void;
};

export const useOpenThreadsStore = createStoreWithPersistAndSubscribe<OpenThreadsStore>(
  (set) => ({
    MAX_TIME_TO_OPEN,
    openThreads: {},
    /**
     * Tracks when a thread is opened by a user
     */
    reportThreadOpen: (lastSms, uiType) => {
      if (!lastSms) {
        return;
      }
      set((state): OpenThreadsStore => {
        const currentThreadState: ThreadState = {
          timeAtInitialOpen: undefined,
          hasReportedTTS: false,
          reportedSmsId: '',
          uiTypes: [],
          ...state.openThreads[lastSms.threadId],
        };
        const secondsToOpen = (Date.now() - new Date(lastSms.createdAt).getTime()) / 1000;
        const maxTimestamp = convertMaxTimeToTimestamp(state.MAX_TIME_TO_OPEN);
        if (
          secondsToOpen >= 0 &&
          currentThreadState.reportedSmsId !== lastSms.id &&
          lastSms.direction === Direction.DIRECTION_INBOUND &&
          lastSms.createdAt > maxTimestamp
        ) {
          pendo.track('sms_thread_tto', {
            secondsToOpen: secondsToOpen.toFixed(1),
            browserWidth: JSON.stringify(document.body.clientWidth),
            uiType,
            threadAlreadyOpen: !!currentThreadState.uiTypes.includes(uiType),
          });
          currentThreadState.reportedSmsId = lastSms.id;
        }

        const updatedThreadState: ThreadState = {
          ...currentThreadState,
          timeAtInitialOpen: currentThreadState.timeAtInitialOpen ?? Date.now(),
          uiTypes: [...new Set([...currentThreadState.uiTypes, uiType])],
        };
        return {
          ...state,
          openThreads: {
            ...state.openThreads,
            [lastSms.threadId]: updatedThreadState,
          },
        };
      });
    },
    /**
     * Tracks when a thread is closed by a user and reports Read Only metrics if needed
     */
    reportThreadClose: (threadId, uiType) => {
      const forcedTimestamp = Date.now();
      // Use timeout to allow the UI to update before reporting the close (so that popping out/in a thread from mini-chats doesn't count as a close)
      setTimeout(() => {
        set((state): OpenThreadsStore => {
          const currentThreadState: ThreadState = {
            timeAtInitialOpen: undefined,
            hasReportedTTS: false,
            reportedSmsId: '',
            uiTypes: [],
            ...state.openThreads[threadId],
          };
          const updatedUITypes = [...currentThreadState.uiTypes].filter((type) => type !== uiType);
          const updatedCount = updatedUITypes.length;
          const updatedThreadState: ThreadState = {
            ...currentThreadState,
            timeAtInitialOpen: updatedCount === 0 ? undefined : currentThreadState.timeAtInitialOpen,
            hasReportedTTS: updatedCount === 0 ? false : currentThreadState.hasReportedTTS,
            uiTypes: updatedUITypes,
          };
          if (
            updatedUITypes.length === 0 &&
            currentThreadState.uiTypes.length !== 0 &&
            !currentThreadState.hasReportedTTS
          ) {
            sendPendoTTSEvent({
              timeAtInitialOpen: currentThreadState.timeAtInitialOpen,
              flowStatus: 'Read Only',
              forcedTimestamp,
            });
          }
          return { ...state, openThreads: { ...state.openThreads, [threadId]: updatedThreadState } };
        });
      }, 500);
    },
    /**
     * Reports Time To Send (TTS) metrics for a thread to pendo when a message is sent or scheduled
     * @param threadId
     * @param flowType - The type of flow that was completed: 'New Manual SMS Sent' | 'New SMS Scheduled' | 'Scheduled Message Sent' | 'Reschedule Message'
     */
    reportTTS: (threadId, flowType) => {
      set((state): OpenThreadsStore => {
        const currentThreadState: ThreadState = {
          timeAtInitialOpen: undefined,
          uiTypes: [],
          hasReportedTTS: false,
          reportedSmsId: '',
          ...state.openThreads[threadId],
        };
        const hasAlreadyReported = !!state.openThreads[threadId]?.hasReportedTTS;
        if (hasAlreadyReported || currentThreadState.timeAtInitialOpen === undefined) return state;

        sendPendoTTSEvent({
          timeAtInitialOpen: currentThreadState.timeAtInitialOpen,
          flowStatus: 'Complete',
          flowType,
        });

        return {
          ...state,
          openThreads: {
            ...state.openThreads,
            [threadId]: {
              ...currentThreadState,
              hasReportedTTS: true,
            },
          },
        };
      });
    },
  }),
  { name: 'OpenThreadsStore', version: 0, storage: createJSONStorage(() => sessionStorage) },
  { name: 'OpenThreads', trace: true }
);

export const useOpenThreadsShallowStore = createShallowStore(useOpenThreadsStore);
